import {
  AkaCriticalIllness,
  AkaGroupLifeCompositions,
  AkaGroupLifeCoverage,
  DisabilityCompositions,
  DisabilityCoverage,
} from "Store/interfaces/products/coverage";

import { getCoveragePrices } from "Components/Features/CheckPrice/Products/Advice/ProductPriceAka/AkaAdviceCards/utils";
import { Price } from "Store/akaAdvice/interfaces/AkaAdviceState";
import { ProductName } from "Store/akaAdvice/interfaces/CollectiveAdvice";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { isDefined } from "Utils/functions";
import { mapOneTimePayment } from "../mapToPriceRequest/mapToDisabilityPriceReq";
import {
  MappingData,
  isAkaCriticalIllnessCoInsuredSelected,
  isAkaCriticalIllnessInsuredSelected,
  isAkaGroupLifeDeathCoInsuredSelected,
  isAkaGroupLifeDeathSelected,
  isAkaGroupLifeIncapacityCoInsuredSelected,
  isAkaGroupLifeIncapacitySelected,
  isAkaGroupLifeTimeCoInsuredSelected,
  isAkaGroupLifeTimeSelected,
  isDisabilityInsuranceSelected,
  isDisabilityPaymentAfter3MonthsSelected,
} from "./mapToCartProducts";

export const mapPrice = (
  isAdvisor: boolean,
  type: ProductName,
  data: MappingData,
): Price => {
  switch (type) {
    case INSURANCE_NAME_VALUE.AKA_GROUP_LIFE:
      return handlePriceAkaGroupLife(data);
    case INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS:
      return handlePriceAkaCriticalIllness(data);
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
      return handlePriceDisabilityFlex(data, isAdvisor);
    default:
      throw Error(`mapPrice - Unable to map prices for type ${type}`);
  }
};

const mergePrices = (prices: Price[]): Price => {
  const totalPrice = {} as Price;
  prices.forEach((price) =>
    Object.entries(price).forEach(
      ([key, value]: [keyof Price, number | string]) => {
        if (typeof value === "number") {
          if (isDefined(totalPrice[key])) {
            totalPrice[key] += value;
          } else {
            totalPrice[key] = value;
          }
        }
      },
    ),
  );
  return totalPrice;
};

const handlePriceAkaGroupLife = (data: MappingData): Price => {
  const composites: AkaGroupLifeCompositions[] = [];

  if (isAkaGroupLifeDeathSelected(data.priceFormData)) {
    composites.push(AkaGroupLifeCompositions.AKA_GROUP_LIFE_INSURED_DEATH);
  }
  if (isAkaGroupLifeIncapacitySelected(data.priceFormData)) {
    composites.push(
      AkaGroupLifeCompositions.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK,
    );

    if (isAkaGroupLifeTimeSelected(data.priceFormData)) {
      composites.push(
        AkaGroupLifeCompositions.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST,
      );
    }
  }

  if (isAkaGroupLifeDeathCoInsuredSelected(data.priceFormData)) {
    composites.push(AkaGroupLifeCompositions.AKA_GROUP_LIFE_CO_INSURED_DEATH);
  }
  if (isAkaGroupLifeIncapacityCoInsuredSelected(data.priceFormData)) {
    composites.push(
      AkaGroupLifeCompositions.AKA_GROUP_LIFE_CO_INSURED_INCAPACITY_OF_WORK,
    );

    if (isAkaGroupLifeTimeCoInsuredSelected(data.priceFormData)) {
      composites.push(
        AkaGroupLifeCompositions.AKA_GROUP_LIFE_CO_INSURED_TIME_LIMITED_DISABILITY_INTEREST,
      );
    }
  }

  const prices = getCoveragePrices(
    [AkaGroupLifeCoverage],
    composites,
    data.priceData,
  );

  return mergePrices(prices);
};

const handlePriceAkaCriticalIllness = (data: MappingData): Price => {
  const composites: AkaCriticalIllness[] = [];

  if (isAkaCriticalIllnessInsuredSelected(data.priceFormData)) {
    composites.push(AkaCriticalIllness.AKA_CRITICAL_ILLNESS);
  }
  if (isAkaCriticalIllnessCoInsuredSelected(data.priceFormData)) {
    composites.push(AkaCriticalIllness.AKA_CRITICAL_ILLNESS_CO_INSURED);
  }

  const prices = getCoveragePrices(
    [AkaCriticalIllness],
    composites,
    data.priceData,
  );

  return mergePrices(prices);
};

const handlePriceDisabilityFlex = (
  data: MappingData,
  isAdvisor: boolean,
): Price => {
  const composites: DisabilityCompositions[] = [];

  if (isDisabilityInsuranceSelected(data.priceFormData)) {
    if (isDisabilityPaymentAfter3MonthsSelected(data.priceFormData)) {
      composites.push(DisabilityCompositions.DISABILITY_PENSION_3MONTHS);
    } else {
      composites.push(DisabilityCompositions.DISABILITY_PENSION);
    }
    const oneTimePayment = mapOneTimePayment(isAdvisor, data.priceFormData);
    if (isDefined(oneTimePayment) && oneTimePayment > 0) {
      composites.push(DisabilityCompositions.DISABILITY_CAPITAL_FLEX);
    }
  }

  const prices = getCoveragePrices(
    [DisabilityCoverage],
    composites,
    data.priceData,
  );

  return mergePrices(prices);
};
